/**
 * Persistent health-check banner.
 *
 * Polls the API /health endpoint and shows a non-dismissable banner when
 * the backend is unreachable or degraded. Disappears automatically when
 * connectivity is restored.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'

import { API_BASE_URL } from '../config.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import { useUiProviders } from '../hooks/useUiProviders.js'
import { type HealthBody, type HealthStatus, healthStatusFrom } from '../utilities/health-status.js'
import { Banner } from './Banner.js'

/** Derive the health endpoint URL from the API base URL. */
function getHealthUrl(): string {
  // API_BASE_URL is typically "/api" or "http://localhost:4000/api"
  // The health endpoint lives at "/health" (root level, not under /api)
  try {
    const url = new URL(API_BASE_URL, window.location.origin)
    return `${url.origin}/health`
  } catch (_error) {
    // Relative path like "/api" — just use origin; URL constructor throws for
    // relative strings without a base that resolves to an absolute URL, which is
    // expected here. The fallback is always safe.
    return `${window.location.origin}/health`
  }
}

/**
 * The API base URL (absolute) for a CORS-immune reachability probe. We hit the
 * base (not /health) so it answers fast even if /health is slow, and we use it
 * with `mode: 'no-cors'` so a cross-origin API that only sends CORS headers on
 * /api routes doesn't make the probe throw.
 */
function getApiProbeUrl(): string {
  try {
    return new URL(API_BASE_URL, window.location.origin).href
  } catch (_error) {
    // Relative path like "/api" — URL constructor throws; fall back to
    // absolute origin + "/api". Safe to ignore: the fallback always works.
    return `${window.location.origin}/api`
  }
}

const POLL_INTERVAL_MS = 30_000
const INITIAL_CHECK_DELAY_MS = 2_000
/** Probe timeout — generous so a momentarily-busy dev server isn't flagged. */
const HEALTH_TIMEOUT_MS = 10_000
/** Timeout for the lightweight no-cors reachability probe (the API base answers fast). */
const REACH_TIMEOUT_MS = 5_000
/** Min consecutive failed probes before the "unreachable" banner can show. */
const FAILURE_THRESHOLD = 3
/** While failing, recheck at this cadence (rather than waiting a full poll). */
const RECHECK_DELAY_MS = 4_000
/**
 * The server must be CONTINUOUSLY unreachable for at least this long before we
 * alarm the user — the fix for false "Cannot reach the server" banners. Transient
 * blips are normal and not outages: the single-process dev API restarts on every
 * code change (tsx watch) and `/health` briefly 502s, and a CPU-saturated server
 * mid-build can make `/health` time out for a few seconds. Requiring a *sustained*
 * outage (not just N failures — which a couple of slow 10s probes rack up fast)
 * keeps those blips silent, while a genuine outage still surfaces within ~30s.
 */
const SUSTAINED_UNREACHABLE_MS = 30_000

/**
 * Decide whether a run of failed `/health` probes is a real outage worth alarming
 * the user about. True only after BOTH enough consecutive failures AND a long
 * enough *continuous* failure window — so transient dev blips (the API restarting
 * on a code change, a brief build-time stall) never trip the scary red banner.
 * @param failures - Count of consecutive failed probes (reset to 0 on any success).
 * @param firstFailureAt - Timestamp (ms) of the first failure in the current run, 0 if none.
 * @param now - Current timestamp (ms).
 * @returns Whether to show the "unreachable" banner.
 */
export function isSustainedOutage(failures: number, firstFailureAt: number, now: number): boolean {
  if (failures < FAILURE_THRESHOLD || firstFailureAt === 0) return false
  return now - firstFailureAt >= SUSTAINED_UNREACHABLE_MS
}

export function HealthBanner() {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [status, setStatus] = useState<HealthStatus>('ok')
  const [dismissed, setDismissed] = useState(false)
  const prevStatusRef = useRef<HealthStatus>('ok')
  const bannerRef = useRef<HTMLDivElement | null>(null)
  const healthUrl = useRef(getHealthUrl())
  const apiProbeUrl = useRef(getApiProbeUrl())
  // This component mounts on EVERY route — including the landing page, whose
  // entry deliberately excludes the ClassMap bonds. The shared Banner calls
  // getClassMap() at render, so rendering it un-wired crashed the whole page
  // ("No UIClassMap has been set"). Gate the visual on the bonds being wired;
  // the hook loads them on demand the moment a banner is actually needed.
  const uiReady = useUiProviders(status !== 'ok' && !dismissed)

  useEffect(() => {
    let active = true
    let failures = 0
    let firstFailureAt = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const schedule = (delay: number): void => {
      if (active) timer = setTimeout(run, delay)
    }

    // Single self-scheduling probe loop. Crucially this never overlaps with
    // itself, so a slow (up to 10s) probe can't race a separate poll timer and
    // double-count failures — the old interval+recheck design let two unrelated
    // blips trip the banner. Healthy → poll every 30s; failing → recheck every
    // few seconds until it recovers OR it's been down long enough to be real.
    const run = async (): Promise<void> => {
      if (!active) return
      let nextDelay = POLL_INTERVAL_MS
      try {
        const res = await fetch(healthUrl.current, {
          signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
        })
        if (!active) return
        failures = 0
        firstFailureAt = 0
        // The server responded (it IS reachable). What it says decides the
        // state — a 503 that only the backups row causes is its own, smaller
        // notice, and a 429 or a deploy's shutting_down says nothing (R92-M7).
        const data = (await res.json().catch(() => null)) as HealthBody | null
        const next = healthStatusFrom({ ok: res.ok, status: res.status }, data)
        if (next !== null) setStatus(next)
      } catch (_error) {
        if (!active) return
        // The /health probe failed — but that may be a CORS block (when the API is a
        // different origin and only /api carries CORS headers) or a slow /health, NOT
        // a real outage. Before alarming, confirm the API is genuinely unreachable
        // with a no-cors probe to the API base: it resolves on ANY response (even
        // opaque/cross-origin/404) and rejects only on a real connection failure.
        // This is the fix for false "Cannot reach the server" banners while the
        // server is actually up.
        let reachable: boolean
        try {
          await fetch(apiProbeUrl.current, {
            mode: 'no-cors',
            signal: AbortSignal.timeout(REACH_TIMEOUT_MS),
          })
          reachable = true
        } catch (_error) {
          // no-cors probe rejects only on a real connection failure — that's the
          // signal we want. Setting reachable=false is the entire purpose here.
          reachable = false
        }
        if (!active) return
        if (reachable) {
          // The server responds — we just couldn't read /health (CORS or slow). Not
          // an outage, so clear any failure run and don't show the banner.
          failures = 0
          firstFailureAt = 0
          setStatus('ok')
        } else {
          // Genuinely no response. A timed-out/refused probe is often just the
          // single-process dev server restarting, so only alarm after enough probes
          // AND a long enough continuous window. Recheck quickly to recover fast.
          failures += 1
          if (firstFailureAt === 0) firstFailureAt = Date.now()
          if (isSustainedOutage(failures, firstFailureAt, Date.now())) {
            setStatus('unreachable')
          }
          nextDelay = RECHECK_DELAY_MS
        }
      }
      schedule(nextDelay)
    }

    // Defer the first check until AFTER the page has loaded, then a short delay.
    // The /health endpoint is cold-start-prone (~2.5s first hit); firing it during
    // initial load puts it on the critical path and inflates LCP. The banner only
    // warns about outages, so a few seconds' delay before the first check is
    // invisible to users but keeps /health off the critical load path.
    const startInitialCheck = (): void => schedule(INITIAL_CHECK_DELAY_MS)
    if (document.readyState === 'complete') startInitialCheck()
    else window.addEventListener('load', startInitialCheck, { once: true })

    return () => {
      active = false
      window.removeEventListener('load', startInitialCheck)
      clearTimeout(timer)
    }
  }, [])

  // When status recovers, auto-dismiss; when it degrades again, un-dismiss
  useEffect(() => {
    if (status === 'ok' && prevStatusRef.current !== 'ok') {
      setDismissed(false)
    }
    if (status !== 'ok' && prevStatusRef.current === 'ok') {
      setDismissed(false)
    }
    prevStatusRef.current = status
  }, [status])

  // Phones: the banner is `position: fixed` at the top, so it paints OVER
  // whatever sits underneath (editor tab strip, chat top). Publish its measured
  // height as `--mol-health-banner-h` on the root element so layout consumers
  // can pad themselves clear of it. This component only PUBLISHES the variable —
  // consumers are wired elsewhere. Layout effect (not plain effect) so the var
  // is set before paint; re-measures on banner resize (message wrap changes on
  // rotate/resize) and removes the property on dismiss/recovery/unmount.
  useLayoutEffect(() => {
    const visible = status !== 'ok' && !dismissed && uiReady
    const root = document.documentElement
    const el = bannerRef.current
    if (!isMobile || !visible || !el) {
      root.style.removeProperty('--mol-health-banner-h')
      return undefined
    }
    const publish = (): void => {
      root.style.setProperty('--mol-health-banner-h', `${el.offsetHeight}px`)
    }
    publish()
    // jsdom has no ResizeObserver — the window resize listener below still
    // covers viewport-driven height changes there.
    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish)
      observer.observe(el)
    }
    window.addEventListener('resize', publish)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', publish)
      root.style.removeProperty('--mol-health-banner-h')
    }
  }, [isMobile, status, dismissed, uiReady])

  if (status === 'ok' || dismissed || !uiReady) return null

  const isUnreachable = status === 'unreachable'

  const message = isUnreachable
    ? t('health.unreachable', undefined, {
        defaultValue:
          'Cannot reach the server. Features may not work until connectivity is restored.',
      })
    : status === 'backups'
      ? t('health.backups', undefined, {
          defaultValue:
            'Project backups cannot be restored right now. Starting a stopped project from its backup may fail; everything else works.',
        })
      : t('health.degraded', undefined, {
          defaultValue: 'Some services are experiencing issues. Performance may be affected.',
        })

  return (
    // The wrapper owns fixed positioning + the height-publishing ref; the
    // shared Banner owns the look (tone wash, centering, top-right ×). The
    // "unreachable" state keeps its high-alarm near-solid fill via
    // `appearance="solid"`.
    <div
      ref={bannerRef}
      style={{
        position: 'fixed',
        // Phones: pin to the TOP edge — the bottom is where the chat composer
        // and bottom nav live, and an ambient status strip must never cover
        // either. Desktop keeps the traditional bottom status bar (offset above
        // the mobile nav via --mol-fixed-bottom-offset when one is mounted).
        ...(isMobile
          ? {
              // Below the page's header when one publishes its bottom edge
              // (workspace bar, global fixed Header); the very top otherwise.
              top: 'var(--mol-fixed-top-offset, env(safe-area-inset-top, 0px))',
              bottom: 'auto',
            }
          : { bottom: 'var(--mol-fixed-bottom-offset, 0px)' }),
        left: 0,
        right: 0,
        // Below sheets/modals (1300-1400): an ambient status strip must never
        // paint OVER an open dialog (it used to punch through at 9999).
        zIndex: 1100,
      }}
    >
      <Banner
        tone={isUnreachable ? 'error' : 'warning'}
        appearance={isUnreachable ? 'solid' : 'tinted'}
        variant="strip"
        // A bottom-docked bar (desktop) carries its border on top.
        edge={isMobile ? 'bottom' : 'top'}
        role="status"
        molId={`health-banner-${status}`}
        message={message}
        onDismiss={() => setDismissed(true)}
        dismissMolId="health-banner-dismiss"
        style={{ backdropFilter: 'blur(8px)' }}
      />
    </div>
  )
}
